export type NamingField = 'module' | 'row' | 'rack';
export type NamingEncoding = 'numeric' | 'alpha' | 'dictionary';

export type NamingProfileComponent =
  | { kind: 'literal'; value: string }
  | {
      kind: 'field';
      field: NamingField;
      encoding: NamingEncoding;
      padding?: number;
      multiplier?: number;
      offset?: number;
      dictionary?: Record<string, string>;
    };

export type NamingProfile = { components: NamingProfileComponent[] };
export type NamingAddress = Partial<Record<NamingField, number>>;
export type NamingProfileVersion = {
  profileId: string;
  version: number;
  name: string;
  profile: NamingProfile;
};
export type NamingProfileCatalog = Record<string, NamingProfileVersion[]>;
export type NamingProfileRef = { profileId: string; version: number };
export type NamingProfileAssignments = {
  default: NamingProfileRef;
  moduleOverrides?: Record<string, NamingProfileRef>;
  zoneOverrides?: Record<string, NamingProfileRef>;
  rowOverrides?: Record<string, NamingProfileRef>;
};

const FIELD_NAMES = new Set<NamingField>(['module', 'row', 'rack']);

export function validateNamingProfile(profile: NamingProfile): void {
  if (!profile || !Array.isArray(profile.components) || profile.components.length === 0) {
    throw new Error('Naming profile must contain at least one component');
  }

  const seenFields = new Set<NamingField>();
  profile.components.forEach((component, index) => {
    if (component.kind === 'literal') {
      if (!component.value) throw new Error(`Component ${index}: literal cannot be empty`);
      return;
    }
    if (component.kind !== 'field' || !FIELD_NAMES.has(component.field)) {
      throw new Error(`Component ${index}: unknown field`);
    }
    if (seenFields.has(component.field)) {
      throw new Error(`Field '${component.field}' appears more than once`);
    }
    seenFields.add(component.field);
    if (!['numeric', 'alpha', 'dictionary'].includes(component.encoding)) {
      throw new Error(`Component ${index}: unknown encoding`);
    }
    if (component.padding !== undefined && (!Number.isInteger(component.padding) || component.padding < 1)) {
      throw new Error(`Component ${index}: padding must be a positive integer`);
    }
    if (component.multiplier !== undefined && (!Number.isInteger(component.multiplier) || component.multiplier < 1)) {
      throw new Error(`Component ${index}: multiplier must be a positive integer`);
    }
    if (component.offset !== undefined && !Number.isInteger(component.offset)) {
      throw new Error(`Component ${index}: offset must be an integer`);
    }
    if (component.encoding === 'alpha' && component.padding !== undefined) {
      throw new Error(`Component ${index}: alpha fields do not support padding`);
    }
    if (component.encoding === 'dictionary') {
      const entries = Object.entries(component.dictionary ?? {});
      if (entries.length === 0) throw new Error(`Component ${index}: dictionary cannot be empty`);
      const values = entries.map(([, value]) => value);
      if (new Set(values).size !== values.length) {
        throw new Error(`Component ${index}: dictionary values must be unique`);
      }
      if (entries.some(([key]) => !Number.isInteger(Number(key)) || Number(key) < 1)) {
        throw new Error(`Component ${index}: dictionary keys must be positive integers`);
      }
    }
  });

  profile.components.forEach((component, index) => {
    const next = profile.components[index + 1];
    if (!next || component.kind !== 'field' || next.kind !== 'field') return;
    const ambiguousWidth = (part: Extract<NamingProfileComponent, { kind: 'field' }>) => {
      if (part.encoding === 'numeric') return part.padding === undefined;
      if (part.encoding === 'dictionary') {
        const lengths = new Set(Object.values(part.dictionary ?? {}).map(value => String(value).length));
        return lengths.size > 1;
      }
      return false;
    };
    if ((component.encoding === 'alpha' && next.encoding === 'alpha')
      || (component.encoding === 'numeric' && next.encoding === 'numeric' && (ambiguousWidth(component) || ambiguousWidth(next)))
      || ((component.encoding === 'dictionary' || next.encoding === 'dictionary') && (ambiguousWidth(component) || ambiguousWidth(next)))) {
      throw new Error('Adjacent variable-width fields are ambiguous; add a literal or padding');
    }
  });
}

function alphaEncode(value: number): string {
  let result = '';
  let n = value;
  while (n > 0) {
    n -= 1;
    result = String.fromCharCode(65 + (n % 26)) + result;
    n = Math.floor(n / 26);
  }
  return result;
}

function alphaDecode(value: string): number {
  return value.toUpperCase().split('').reduce((total, char) => total * 26 + char.charCodeAt(0) - 64, 0);
}

function transformedValue(value: number, component: Extract<NamingProfileComponent, { kind: 'field' }>): number {
  if (!Number.isInteger(value) || value < 1) throw new Error(`Field '${component.field}' must be a positive integer`);
  return (value + (component.offset ?? 0)) * (component.multiplier ?? 1);
}

function inverseValue(value: number, component: Extract<NamingProfileComponent, { kind: 'field' }>): number {
  const multiplier = component.multiplier ?? 1;
  const decoded = value / multiplier - (component.offset ?? 0);
  if (!Number.isInteger(decoded) || decoded < 1) throw new Error(`Value is invalid for field '${component.field}'`);
  return decoded;
}

export function formatNamingProfile(profile: NamingProfile, address: NamingAddress): string {
  validateNamingProfile(profile);
  return profile.components.map(component => {
    if (component.kind === 'literal') return component.value;
    const value = address[component.field];
    if (value === undefined) throw new Error(`Address is missing '${component.field}'`);
    if (component.encoding === 'dictionary') {
      const result = component.dictionary?.[String(value)];
      if (result === undefined) throw new Error(`Dictionary has no value for '${component.field}=${value}'`);
      return result;
    }
    const transformed = transformedValue(value, component);
    const text = component.encoding === 'alpha' ? alphaEncode(transformed) : String(transformed);
    return component.padding ? text.padStart(component.padding, '0') : text;
  }).join('');
}

export function parseNamingProfile(profile: NamingProfile, input: string): NamingAddress {
  validateNamingProfile(profile);
  type Candidate = { position: number; address: NamingAddress };
  const parse = (index: number, position: number, address: NamingAddress): Candidate[] => {
    const component = profile.components[index];
    if (!component) return position === input.length ? [{ position, address }] : [];
    if (component.kind === 'literal') {
      return input.startsWith(component.value, position)
        ? parse(index + 1, position + component.value.length, address)
        : [];
    }

    const candidates: Candidate[] = [];
    const addField = (raw: string, nextPosition: number) => {
      try {
        const next = { ...address };
        if (component.encoding === 'dictionary') {
          const key = Object.entries(component.dictionary ?? {}).find(([, value]) => value === raw)?.[0];
          if (!key) return;
          next[component.field] = Number(key);
        } else {
          const value = component.encoding === 'alpha' ? alphaDecode(raw) : Number(raw);
          next[component.field] = inverseValue(value, component);
        }
        candidates.push(...parse(index + 1, nextPosition, next));
      } catch {
        // A candidate can be structurally valid but fail multiplier/offset rules.
      }
    };

    if (component.encoding === 'dictionary') {
      for (const value of Object.values(component.dictionary ?? {})) {
        if (input.startsWith(value, position)) addField(value, position + value.length);
      }
    } else if (component.encoding === 'alpha') {
      const match = /^[A-Za-z]+/.exec(input.slice(position));
      if (match) {
        for (let length = 1; length <= match[0].length; length += 1) {
          addField(match[0].slice(0, length), position + length);
        }
      }
    } else {
      const match = /^\d+/.exec(input.slice(position));
      if (match) {
        const minimum = component.padding ?? 1;
        for (let length = minimum; length <= match[0].length; length += 1) {
          addField(match[0].slice(0, length), position + length);
        }
      }
    }
    return candidates;
  };

  const results = parse(0, 0, {});
  const canonical = results.filter(result => formatNamingProfile(profile, result.address) === input);
  const unique = canonical.filter((result, index, all) => all.findIndex(item => JSON.stringify(item.address) === JSON.stringify(result.address)) === index);
  if (unique.length === 1) return unique[0].address;
  if (unique.length > 1) throw new Error(`Address '${input}' is ambiguous for naming profile`);
  throw new Error(`Address '${input}' does not match naming profile`);
}

export function createNamingProfileVersion(input: {
  profileId: string;
  name: string;
  profile: NamingProfile;
  previous?: NamingProfileVersion;
}): NamingProfileVersion {
  validateNamingProfile(input.profile);
  if (input.previous && input.previous.profileId !== input.profileId) {
    throw new Error('Previous version belongs to another profile');
  }
  return {
    profileId: input.profileId,
    version: (input.previous?.version ?? 0) + 1,
    name: input.name.trim() || input.profileId,
    profile: structuredClone(input.profile),
  };
}

export function resolveNamingProfileVersion(
  catalog: NamingProfileCatalog,
  assignments: NamingProfileAssignments,
  scope: { rowId?: string; zoneId?: string; moduleId?: string } = {},
): NamingProfileVersion {
  const ref = (scope.rowId && assignments.rowOverrides?.[scope.rowId])
    || (scope.zoneId && assignments.zoneOverrides?.[scope.zoneId])
    || (scope.moduleId && assignments.moduleOverrides?.[scope.moduleId])
    || assignments.default;
  if (!ref) throw new Error('Naming profile assignment has no default version');
  const version = catalog[ref.profileId]?.find(item => item.version === ref.version);
  if (!version) throw new Error(`Naming profile version '${ref.profileId}@${ref.version}' is not available`);
  return version;
}

export function validateNamingProfileCatalog(
  catalog: NamingProfileCatalog,
  assignments?: NamingProfileAssignments,
): void {
  Object.entries(catalog).forEach(([profileId, versions]) => {
    if (!versions.length) throw new Error(`Profile '${profileId}' has no versions`);
    const seen = new Set<number>();
    versions.forEach(version => {
      if (version.profileId !== profileId || seen.has(version.version) || version.version < 1) {
        throw new Error(`Profile '${profileId}' has invalid or duplicate versions`);
      }
      seen.add(version.version);
      validateNamingProfile(version.profile);
    });
  });
  if (assignments) resolveNamingProfileVersion(catalog, assignments);
}

